var _dec, _dec2, _dec3, _class, _MenuItem, _IconCheckSolid, _IconArrowOpenEndSoli;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import keycode from 'keycode';
import { IconCheckSolid, IconArrowOpenEndSolid } from '@instructure/ui-icons';
import { omitProps, getElementType, withDeterministicId, callRenderProp } from '@instructure/ui-react-utils';
import { createChainedFunction } from '@instructure/ui-utils';
import { isActiveElement, findDOMNode } from '@instructure/ui-dom-utils';
import { testable } from '@instructure/ui-testable';
import { withStyle } from '@instructure/emotion';
import { MenuContext } from '../../MenuContext';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
---
parent: Menu
id: Menu.Item
---
**/
let MenuItem = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_MenuItem = class MenuItem extends Component {
  constructor(props) {
    super(props);
    this.labelId = void 0;
    this.ref = null;
    this.handleRef = el => {
      this.ref = el;
    };
    this.handleClick = e => {
      const _this$props = this.props,
        onSelect = _this$props.onSelect,
        onClick = _this$props.onClick,
        disabled = _this$props.disabled,
        value = _this$props.value;
      const selected = !this.selected;
      if (disabled) {
        e.preventDefault();
        return;
      }
      if (typeof this.props.selected === 'undefined') {
        this.setState({
          selected
        });
      }
      if (typeof onSelect === 'function') {
        e.persist();
        onSelect(e, value, selected, this);
      }
      if (typeof onClick === 'function') {
        onClick(e);
      }
    };
    this.handleKeyDown = e => {
      const spaceKey = e.keyCode === keycode.codes.space;
      const enterKey = e.keyCode === keycode.codes.enter;
      if (spaceKey || enterKey) {
        e.preventDefault();
        e.stopPropagation();
        if (enterKey) {
          // handle space key on keyUp for FF
          const refNode = findDOMNode(this.ref);
          refNode.click();
        }
      }
    };
    this.handleKeyUp = e => {
      const spaceKey = e.keyCode === keycode.codes.space;
      const enterKey = e.keyCode === keycode.codes.enter;
      if (spaceKey || enterKey) {
        e.preventDefault();
        e.stopPropagation();
        if (spaceKey) {
          const refNode = findDOMNode(this.ref);
          refNode.click();
        }
      }
    };
    this.handleMouseOver = event => {
      if (!this.focused) {
        this.focus();
      }
      if (typeof this.props.onMouseOver === 'function') {
        this.props.onMouseOver(event, this);
      }
    };
    if (typeof props.selected === 'undefined') {
      this.state = {
        selected: !!props.defaultSelected
      };
    }
    this.labelId = props.deterministicId('MenuItem__label');
  }
  get _node() {
    return this.ref;
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props2;
    (_this$props$makeStyle = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props2);
    const context = this.context;
    if (context && context.registerMenuItem) {
      context.registerMenuItem(this);
    }
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props3;
    (_this$props$makeStyle2 = (_this$props3 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props3);
  }
  componentWillUnmount() {
    const context = this.context;
    if (context && context.removeMenuItem) {
      context.removeMenuItem(this);
    }
  }
  get elementType() {
    return getElementType(MenuItem, this.props);
  }
  get role() {
    switch (this.props.type) {
      case 'checkbox':
        return 'menuitemcheckbox';
      case 'radio':
        return 'menuitemradio';
      default:
        return 'menuitem';
    }
  }
  get selected() {
    return typeof this.props.selected === 'undefined' ? this.state.selected : this.props.selected;
  }
  get focused() {
    return isActiveElement(this.ref);
  }
  focus() {
    const refNode = findDOMNode(this.ref);
    refNode.focus();
  }
  renderContent() {
    var _this$props$styles, _this$props$styles2, _this$props$styles3, _this$props$styles4;
    const _this$props4 = this.props,
      children = _this$props4.children,
      type = _this$props4.type,
      renderLabelInfo = _this$props4.renderLabelInfo;
    return _jsxs("span", {
      children: [(type === 'checkbox' || type === 'radio') && _jsx("span", {
        css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.icon,
        children: this.selected && (_IconCheckSolid || (_IconCheckSolid = _jsx(IconCheckSolid, {})))
      }), _jsx("span", {
        css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.label,
        id: this.labelId,
        children: children
      }), type === 'flyout' && _jsx("span", {
        css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.icon,
        children: _IconArrowOpenEndSoli || (_IconArrowOpenEndSoli = _jsx(IconArrowOpenEndSolid, {}))
      }), renderLabelInfo && _jsx("span", {
        css: (_this$props$styles4 = this.props.styles) === null || _this$props$styles4 === void 0 ? void 0 : _this$props$styles4.labelInfo,
        children: callRenderProp(renderLabelInfo)
      })]
    });
  }
  render() {
    var _this$props$styles5;
    const _this$props5 = this.props,
      disabled = _this$props5.disabled,
      controls = _this$props5.controls,
      onKeyDown = _this$props5.onKeyDown,
      onKeyUp = _this$props5.onKeyUp,
      type = _this$props5.type,
      href = _this$props5.href,
      target = _this$props5.target;
    const props = omitProps(this.props, MenuItem.allowedProps);
    const ElementType = this.elementType;
    return _jsx(ElementType, {
      tabIndex: -1 // note: tabIndex can be overridden by Menu or MenuItemGroup components
      ,
      ...props,
      href: href,
      target: target,
      role: this.role,
      "aria-labelledby": this.labelId,
      "aria-disabled": disabled ? 'true' : void 0,
      "aria-controls": controls,
      "aria-checked": type === 'checkbox' || type === 'radio' ? this.selected ? 'true' : 'false' : void 0,
      onClick: this.handleClick,
      onKeyUp: createChainedFunction(onKeyUp, this.handleKeyUp),
      onKeyDown: createChainedFunction(onKeyDown, this.handleKeyDown),
      ref: this.handleRef,
      css: (_this$props$styles5 = this.props.styles) === null || _this$props$styles5 === void 0 ? void 0 : _this$props$styles5.menuItem,
      onMouseOver: this.handleMouseOver,
      children: this.renderContent()
    });
  }
}, _MenuItem.displayName = "MenuItem", _MenuItem.componentId = 'Menu.Item', _MenuItem.propTypes = propTypes, _MenuItem.allowedProps = allowedProps, _MenuItem.defaultProps = {
  type: 'button',
  disabled: false
}, _MenuItem.contextType = MenuContext, _MenuItem)) || _class) || _class) || _class);
export default MenuItem;
export { MenuItem };